import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { StatCard } from "@/components/ui/stat-card";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { acquisitionsApi, type FundDetail } from "@/lib/api/acquisitions";
import { Loader2, Pencil, DollarSign, PiggyBank, TrendingUp, Wallet } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";

const txTypeVariants: Record<
  string,
  { variant: "success" | "warning" | "info" | "danger" | "neutral" | "purple"; label: string }
> = {
  ALLOCATION: { variant: "success", label: "Allocation" },
  ENCUMBRANCE: { variant: "warning", label: "Encumbrance" },
  EXPENDITURE: { variant: "info", label: "Expenditure" },
  RELEASE: { variant: "purple", label: "Release" },
  TRANSFER: { variant: "neutral", label: "Transfer" },
  ADJUSTMENT: { variant: "neutral", label: "Adjustment" },
};

export default function FundDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [fund, setFund] = useState<FundDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    acquisitionsApi.funds
      .getById(id)
      .then(setFund)
      .catch(() => toast.error("Failed to load fund."))
      .finally(() => setLoading(false));
  }, [id]);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(n);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!fund) {
    return <div className="py-20 text-center text-slate-500">Fund not found</div>;
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={fund.name}
        description={`Code: ${fund.code} | FY ${fund.fiscalYear}`}
        breadcrumbItems={[{ label: "Budget", href: "/acquisitions/budget" }, { label: fund.name }]}
        showSearch={false}
        titleSize="md"
        actions={
          <Button onClick={() => navigate(`/acquisitions/budget/funds/${fund.id}/edit`)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          icon={<DollarSign className="h-5 w-5" />}
          label="Allocation"
          value={formatCurrency(fund.allocation)}
        />
        <StatCard
          icon={<Wallet className="h-5 w-5" />}
          label="Encumbered"
          value={formatCurrency(fund.encumbered)}
          iconBg="bg-amber-50 text-amber-600"
        />
        <StatCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="Expended"
          value={formatCurrency(fund.expended)}
          iconBg="bg-blue-50 text-blue-600"
        />
        <StatCard
          icon={<PiggyBank className="h-5 w-5" />}
          label="Available"
          value={formatCurrency(fund.available)}
          iconBg={fund.available < 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}
        />
      </div>

      <div className="flex items-center gap-3">
        <StatusBadge variant={fund.isActive ? "success" : "neutral"}>
          {fund.isActive ? "Active" : "Inactive"}
        </StatusBadge>
        {fund.branch && <span className="text-sm text-slate-500">Branch: {fund.branch.name}</span>}
        <span className="text-sm text-slate-500">Utilization: {fund.utilizationPercent}%</span>
      </div>

      {fund.description && (
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-slate-600">{fund.description}</p>
        </div>
      )}

      <div className="rounded-lg border bg-white">
        <div className="border-b px-4 py-3">
          <h3 className="font-semibold text-slate-900">Transaction History</h3>
        </div>
        {fund.transactions.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">No transactions yet</div>
        ) : (
          <div className="divide-y">
            {fund.transactions.map((tx) => {
              const cfg = txTypeVariants[tx.type] || {
                variant: "neutral" as const,
                label: tx.type,
              };
              return (
                <div key={tx.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>
                    <span className="text-sm text-slate-600">{tx.description || "—"}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`text-sm font-semibold tabular-nums ${
                        tx.amount < 0 ? "text-red-600" : "text-slate-900"
                      }`}
                    >
                      {tx.amount > 0 ? "+" : ""}
                      {formatCurrency(tx.amount)}
                    </span>
                    <span className="text-xs text-slate-400">
                      {new Date(tx.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
